import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { agentKeys } from "@/shared/api/agents";
import type { ContextAgent, Message } from "@/shared/types";
import { pickOf } from "../contextPick";
import type { ContextPick } from "../types";

export interface ContextFollow {
  /** This is Builder's chat: nothing else has a picker to keep. */
  on: boolean;
  chatId: string | undefined;
  /** The conversation a message from this chat just started, set as `POST /runs` answers;
      taken (cleared) once the chat is on it. */
  ownChat: { current: string | null };
  /** The conversation's first page of messages is read (`ChatSource.read`). */
  read: boolean;
  messages: Message[];
  /** The workspace's agents; undefined until read. */
  agents: ContextAgent[] | undefined;
  /** The agents list is being read again: an agent missing from it may only be new. */
  agentsFetching: boolean;
  pick: ContextPick;
  setPick: (pick: ContextPick) => void;
}

/**
 * Keeps Builder's agent picker in step with its conversation and the workspace's agents.
 *
 * Opening a conversation (the chat mounting on it, or switching to it) restores the agent its
 * last message was about, once that conversation's first page and the agents are read: under the
 * name it has now, or nothing once deleted. A conversation this chat's own first message started
 * (`ownChat`) keeps what was picked. When Builder makes an agent in the newest turn (`create_agent`'s
 * `meta.made_agent`), the picker moves to it and the agents are read again so it shows; agents made
 * in turns already there when a conversation was opened move nothing. A picked agent deleted
 * meanwhile drops back to nothing, and a renamed one takes its new name.
 */
export default function useContextFollow({ on, chatId, ownChat, read, messages, agents, agentsFetching, pick, setPick }: ContextFollow): void {
  const qc = useQueryClient();
  const restored = useRef<string | null>(null);
  const seen = useRef(new Set<string>());
  /** Agents Builder made here: not in the list until it is read again. */
  const made = useRef(new Set<string>());

  const madeIn = (message?: Message) =>
    (message?.parts ?? []).flatMap((part) => (part.kind === "tool" && part.step.madeAgent ? [{ step: part.step.id, agent: part.step.madeAgent }] : []));

  useEffect(() => {
    if (!on || !chatId || restored.current === chatId) return;
    if (ownChat.current === chatId) {
      ownChat.current = null; // a later return to it restores like any other
      restored.current = chatId; // its own new conversation keeps what was picked
      return;
    }
    if (!read || !agents) return;
    restored.current = chatId;
    messages.forEach((m) => madeIn(m).forEach(({ step }) => seen.current.add(step)));
    // its last message the first page holds; a page of one long turn's steps holds none
    const about = messages.findLast((m) => m.role === "user")?.contextAgent;
    const agent = about && agents.find((a) => a.id === about.id);
    setPick(agent ? pickOf(agent) : null);
  }, [on, chatId, ownChat, read, messages, agents, setPick]);

  // Only the newest turn: older pages read on scroll-up go in above it.
  const newest = messages.findLast((m) => m.role === "agent");
  useEffect(() => {
    if (!on || restored.current !== chatId) return;
    for (const { step, agent } of madeIn(newest)) {
      if (seen.current.has(step)) continue;
      seen.current.add(step);
      made.current.add(agent.id);
      setPick({ kind: "agent", agent });
      qc.invalidateQueries({ queryKey: agentKeys.all });
    }
  }, [on, chatId, newest, setPick, qc]);

  const picked = pick?.kind === "agent" ? pick.agent : null;
  useEffect(() => {
    if (!on || !picked || !agents || agentsFetching) return;
    const now = agents.find((a) => a.id === picked.id);
    if (now) {
      made.current.delete(picked.id);
      if (now.name !== picked.name) setPick(pickOf(now));
    } else if (!made.current.has(picked.id)) {
      setPick(null); // deleted: every send would be refused
    }
  }, [on, picked, agents, agentsFetching, setPick]);
}
